import React, {useEffect, useState} from 'react';
import CRUDTable from "../common/htmlElements/TableComponent/subTableComponents/CRUDTable";
import {Link} from "react-router-dom";
import {injectIntl} from "react-intl";
import moment from 'moment';
import "./ShowUsersTable.css"
import PopperWindow from "../PopperWindow/PopperWindow";
import {getWindowWidthAndHeight} from "../../../utility/javascriptTools.mjs";
import {Dropdown} from "react-bootstrap";
import LoadClientForm from "../Forms/Client/LoadClientForm";
import MatchClientPageContent from "../Pages/MatchClientPage/MatchClientPageContent";
import LoadSponsorForm from "../Forms/Sponsor/LoadSponsorForm";
import {confirmAlert} from "react-confirm-alert";
import {deleteMatchFromClientID} from "../../../services/matchUsersService";

function ShowUsersTable ({userType, userList, onSort, sortColumn, isLoading, locale, onSelectElement, intl, userConfirmationFunction, isLoadingEmailConfirmation}) {
    const USER_APPROVED_STATE = 2;

    const { windowWidth, windowHeight } = getWindowWidthAndHeight();
    const [usersList, setUsersList] = useState(userList);
    const [columns, setColumns] = useState([]);
    const [renderCrudTable, setRenderCrudTable] = useState();
    const [editClientWindowState, setEditClientWindowState] = useState(false);
    const [editSponsorWindowState, setEditSponsorWindowState] = useState(false);
    const [matchClientWindowState, setMatchClientWindowState] = useState(false);
    const [userID, setUserID] = useState();

    const confirmUserMatch = async (user) =>{
        userConfirmationFunction(user);
    }

    const getConfirmationFlags = (confirmationStatus) => {
        return {
            studentConfirmed: confirmationStatus === 1 || confirmationStatus === 3,
            sponsorConfirmed: confirmationStatus === 2 || confirmationStatus === 3
        };
    };

    const renderConfirmationStatus = (user) => {
        // For clients: only show confirmation data when the profile is confirmed
        if (userType === "clients" && user.state !== 10) {
            return null;
        }

        const {studentConfirmed, sponsorConfirmed} = getConfirmationFlags(user.confirmation_Status);
        const studentLabel = intl.formatMessage({id: "table.users.confirmation.student.short"});
        const sponsorLabel = intl.formatMessage({id: "table.users.confirmation.sponsor.short"});
        const studentBadgeLabel = intl.formatMessage({id: "table.users.confirmation.student.abbr"});
        const sponsorBadgeLabel = intl.formatMessage({id: "table.users.confirmation.sponsor.abbr"});
        const confirmedLabel = intl.formatMessage({id: "table.users.confirmation.confirmed.short"});
        const pendingLabel = intl.formatMessage({id: "table.users.confirmation.pending.short"});
        const tooltip = `${studentLabel}: ${studentConfirmed ? confirmedLabel : pendingLabel} | ${sponsorLabel}: ${sponsorConfirmed ? confirmedLabel : pendingLabel}`;

        const renderStatusPill = (label, isConfirmed) => (
            <span className={`confirmationStatusPill ${isConfirmed ? "isConfirmed" : "isPending"}`}>
                <span className="confirmationStatusName">{label}</span>
                <span className="confirmationStatusValue">{isConfirmed ? confirmedLabel : pendingLabel}</span>
            </span>
        );

        return (
            <div className="confirmationStatusCell" title={tooltip} aria-label={tooltip}>
                {renderStatusPill(studentBadgeLabel, studentConfirmed)}
                {renderStatusPill(sponsorBadgeLabel, sponsorConfirmed)}
            </div>
        );
    };

    const getRegistrationAgeMetadata = (registrationDate) => {
        const dayCount = Math.max(moment().startOf('day').diff(moment(registrationDate).startOf('day'), 'days'), 0);

        if (dayCount <= 2) {
            return {level: "new", labelId: "table.users.registrationAge.new", dayCount};
        }

        if (dayCount <= 7) {
            return {level: "recent", labelId: "table.users.registrationAge.recent", dayCount};
        }

        if (dayCount <= 14) {
            return {level: "attention", labelId: "table.users.registrationAge.attention", dayCount};
        }

        return {level: "priority", labelId: "table.users.registrationAge.priority", dayCount};
    };

    const renderRegistrationAge = (user) => {
        if (!user.date_registered) {
            return null;
        }

        const registrationDate = moment(user.date_registered);
        const formattedRegistrationDate = registrationDate.locale(locale).format('ll');
        const ageMetadata = getRegistrationAgeMetadata(user.date_registered);
        const badgeLabel = intl.formatMessage({id: ageMetadata.labelId});
        const daysLabel = intl.formatMessage(
            {id: "table.users.registrationAge.days"},
            {count: ageMetadata.dayCount}
        );
        const daysAgoLabel = intl.formatMessage(
            {id: "table.users.registrationAge.daysAgo"},
            {count: ageMetadata.dayCount}
        );
        const secondaryAgeLabel = ageMetadata.level === "new" ? daysAgoLabel : daysLabel;
        const tooltip = `${formattedRegistrationDate} | ${badgeLabel}: ${secondaryAgeLabel}`;

        return (
            <div className="registrationAgeCell" title={tooltip} aria-label={tooltip}>
                <span className="registrationDatePrimary">{formattedRegistrationDate}</span>
                <span className={`registrationAgeSecondary ${ageMetadata.level}`}>
                    <span className="registrationAgeDot" aria-hidden="true"></span>
                    <span className="registrationAgeDaysSecondary">Il y a {secondaryAgeLabel}</span>
                </span>
            </div>
        );
    };

    const renderClientActions = (user) => {
        const confirmLabel = (user.state === 10)
            ? intl.formatMessage({id: "table.users.ResendConfirmLink"})
            : intl.formatMessage({id: "table.users.confirmLink"});

        return (
            <div className="tableActionsCell">
                <Dropdown className="tableActionDropdown" align="end">
                    <Dropdown.Toggle className="tableActionMenuToggle" id={`actions-client-${user.id}`}>
                        {intl.formatMessage({id: "table.users.actions.button"})}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="tableActionMenu">
                        <Dropdown.Item onClick={() => handleEditClientButtonClick(user)}>
                            {intl.formatMessage({id: "table.users.editLink"})}
                        </Dropdown.Item>

                        {user.takenInChargeByID &&
                            <Dropdown.Item onClick={() => handleDeleteMatchButtonClick(user)}>
                                {intl.formatMessage({id: "table.users.deleteMatchLink"})}
                            </Dropdown.Item>
                        }

                        {user.state === USER_APPROVED_STATE &&
                            <Dropdown.Item onClick={() => handleMatchButtonClick(user)}>
                                {intl.formatMessage({id: "table.users.matchLink"})}
                            </Dropdown.Item>
                        }

                        {user.takenInChargeByID &&
                            <>
                                <Dropdown.Divider />
                                <Dropdown.Item as={Link} to={`./clientlist`} onClick={async ()=> { await confirmUserMatch(user); }}>
                                    {confirmLabel}
                                </Dropdown.Item>
                            </>
                        }
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        );
    };

    const renderSponsorActions = (user) => {
        return (
            <div className="tableActionsCell sponsorActionsCell">
                <Dropdown className="tableActionDropdown" align="end">
                    <Dropdown.Toggle className="tableActionMenuToggle" id={`actions-sponsor-${user.id}`}>
                        {intl.formatMessage({id: "table.users.actions.button"})}
                    </Dropdown.Toggle>

                    <Dropdown.Menu className="tableActionMenu">
                        <Dropdown.Item onClick={() => handleEditSponsorButtonClick(user)}>
                            {intl.formatMessage({id: "table.users.editLink"})}
                        </Dropdown.Item>
                    </Dropdown.Menu>
                </Dropdown>
            </div>
        );
    };

    const createColumns = () => {
        let columns;
        columns = [
            //{path:"id",label:intl.formatMessage({id: "table.users.id.short"})},
        ];

        // Only show confirmation status column for clients
        if (userType === "clients") {
            columns.push({
                key: "status", content: user => renderConfirmationStatus(user),
                label: intl.formatMessage({id: "table.users.confirmations.short"})
            });
        }

        columns.push({key: "date_registered", content: user => renderRegistrationAge(user), label: intl.formatMessage({id: "table.users.dateCreated.short"})});
        columns.push({path: "ni", label: intl.formatMessage({id: "table.users.ni.short"})});
        columns.push({path: "firstName", label: intl.formatMessage({id: "table.users.firstname.short"})});
        columns.push({path: "lastName", label: intl.formatMessage({id: "table.users.lastname.short"})});
        columns.push({path: "state", label: intl.formatMessage({id: "table.users.state.short"})});

        switch (userType){

            case "clients" :
                columns.push(
                    {path:"takenInChargeByName",label:intl.formatMessage({id: "table.users.takenInChargeByName.short"})},
                    {path:"arrivalInQuebec",label:intl.formatMessage({id: "table.users.arrivalInQuebec.short"})},
                    {key:"actions", content: user => renderClientActions(user), label:"Actions"}
                );

                break;

            case "sponsors" :
                columns.push({path:"inChargeOfClientsNames",label:intl.formatMessage({id: "table.users.takenInChargeByName.short"})});
                columns.push({key:"actions", content: user => renderSponsorActions(user), label:"Actions"});
                break;

            default:
                break;
        }

        return columns;
    };

    const renderNewCrudTable = () => {
        return <CRUDTable
            columns={columns}
            data = {usersList}
            sortColumn={sortColumn}
            onSort={onSort}
            isLoading={isLoading}
            locale = {locale}
            select ={true}
            onSelectElement = {onSelectElement}
            handleMatchButtonClick = {handleMatchButtonClick}
        />;
    };

    useEffect(() => {
        setUsersList(userList);
    }, [userList]);

    useEffect(() => {
        setColumns(createColumns());
    }, [usersList]);

    useEffect(() => {
        if(columns.length !== 0) {
            setRenderCrudTable(renderNewCrudTable());
        }
    }, [columns, sortColumn]);

    const hideEditClientWindow = () => {
        setEditClientWindowState(false);
    }

    const hideEditSponsorWindow = () => {
        setEditSponsorWindowState(false);
    }

    const hideMatchClientWindow = () => {
        setMatchClientWindowState(false);
    }

    const handleEditClientButtonClick = (user) => {
        setUserID(user.id);
        setEditClientWindowState(true);
    }

    const deleteMatchInUserListState = (userID) => {

        const updatedUsers = usersList.map((user) => {

            if (user.id === userID) {
                return { ...user, confirmation_Status: 0, takenInChargeByID: null, takenInChargeByName: null, state:2 };
            }
            return user;

        });
        setUsersList(updatedUsers);
    }


    const handleDeleteMatchButtonClick = (user) => {

        const doDeleteMatch = async () =>{
            try {
                deleteMatchFromClientID(user.id);
                deleteMatchInUserListState(user.id);
            }catch (e) {
                console.error(e.message);
            }
        }

        confirmAlert({
            customUI: ({ onClose }) => {
                return (
                    <div className='react-confirm-alert-blur-custom-ui smallWindow'>
                        <p className="bigMessage">Souhaitez-vous vraiment supprimer le jumelage de {user.firstName} {user.lastName}?</p>
                        <p>Actuellement pris en charge par {user.takenInChargeByName}</p>
                        <button className="redButton" onClick={async () => {await doDeleteMatch();onClose();}}>Oui</button>
                        <button className="redButton" style={{marginRight:10}} onClick={onClose}>Non</button>
                    </div>
                );
            }
        });



    }

    const handleEditSponsorButtonClick = (user) => {
        setUserID(user.id);
        setEditSponsorWindowState(true);
    }

    const handleMatchButtonClick = (user) => {
        setUserID(user.id);
        setMatchClientWindowState(true);
    }

    return[
        <React.Fragment key="users-table-main">

            <h1 className="userListTitle">{userType === "clients"? "Jumeler les étudiants de l'international":"Gestion des parrains et marraines"}</h1>

            <div className="modernUsersTable">
                {renderCrudTable}
            </div>

        </React.Fragment>
        ,
        <div key="users-table-poppers">
            <PopperWindow
                modalTitle={"Formulaire - Étudiant de l'international"}
                modalContent={
                    <LoadClientForm
                        locale={locale}
                        id={userID}
                        usersList={usersList}
                        setUsersList={setUsersList}
                        setEditClientWindowState={setEditClientWindowState}
                    />
                }
                saveText="Sauvegarder"
                saveColor="success"
                cancelText="Annuler"
                width={Math.max(windowWidth*0.4, 1000)}
                height={Math.max(windowHeight*0.4, 720)}
                showValue={editClientWindowState}
                closeWindow={hideEditClientWindow}
                isForm={true}
                isLargeWidth={true}
            />
            <PopperWindow
                modalTitle={"Formulaire - Jumelage"}
                modalContent={
                    <MatchClientPageContent
                        locale={locale}
                        clientID={userID}
                        popupHeight={Math.max(windowHeight*0.75, 720)}
                        usersList={usersList}
                        setUsersList={setUsersList}
                        setMatchClientWindowState={setMatchClientWindowState}
                        deleteMatchInUserListState ={deleteMatchInUserListState}
                    />
                }
                saveText="Sauvegarder"
                saveColor="success"
                cancelText="Annuler"
                width={Math.max(windowWidth*0.8, 1000)}
                height={Math.max(windowHeight*0.75, 720)}
                showValue={matchClientWindowState}
                closeWindow={hideMatchClientWindow}
                isForm={true}
                isLargeWidth={true}
            />
            <PopperWindow
                modalTitle={"Formulaire - Parrain/marraine"}
                modalContent={
                    <LoadSponsorForm
                        locale={locale}
                        id={userID}
                        usersList={usersList}
                        setUsersList={setUsersList}
                        setEditSponsorWindowState={setEditSponsorWindowState}
                    />
                }
                saveText="Sauvegarder"
                saveColor="success"
                cancelText="Annuler"
                width={Math.max(windowWidth*0.4, 1000)}
                height={Math.max(windowHeight*0.4, 720)}
                showValue={editSponsorWindowState}
                closeWindow={hideEditSponsorWindow}
                isForm={true}
                isLargeWidth={true}
            />
        </div>
    ];



}
export default injectIntl(ShowUsersTable);
